import { Button, CardMedia, Card, makeStyles, Typography, TextField } from "@material-ui/core";
import { Wrapper } from "../base/Wrapper";
import TitleBar from "../TitleBar";
import RadarChart from "../RadarChart";
import { useVerified } from "../../hooks/useVerified";
import ErrorPage from "../ErrorPage/ErrorPage";
import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_EXERCISE_DETAILS } from "../../GraphQL/Queries";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";

const useStyles = makeStyles((theme) => ({
  content: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    margin: theme.spacing(1, 0),
    marginTop: theme.spacing(2),
  },
  repetitionCard: {
    padding: "0 10px 10px 10px",
    position: "fixed",
    width: "90%",
    bottom: 70,
    boxShadow: theme.shadows[3],
  },
  list: {
    listStyleType: "none",
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
  listItem: {
    fontFamily: "'Inter', sans-serif",
    paddingBottom: theme.spacing(0.3),
    "&:before": {
      content: "'• '",
      color: "#6999dd",
      fontWeight: "bold",
      fontSize: 15,
      display: "inline-block",
      width: "1em",
      marginLeft: "-1em",
    },
  },
  chartContainer: {
    paddingBottom: theme.spacing(10),
    zIndex: "-1",
  },
  repetitionContainer: {
    display: "flex",
    alignItems: "flex-end",
    paddingTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  repetitionTitle: {
    flex: "1 1 85%",
    paddingBottom: theme.spacing(0.35),
  },
  selector: {
    flex: "2 2 15%",
    paddingTop: theme.spacing(1),
  },
  resize: {
    fontSize: "0.875rem",
    padding: theme.spacing(0.5),
    textAlign: "center",
  },
}));

function ExerciseDetails({ stNr, exName, navigate }) {
  const classes = useStyles();

  const [station, setStation] = useState();
  const [repetition, setRepetition] = useState(1);
  const { data, isLoading, error } = useQuery(GET_EXERCISE_DETAILS, {
    variables: { stNr: parseInt(stNr), exName },
  });
  const { verifiedString } = useVerified();

  useEffect(() => {
    if (!isLoading && data) {
      setStation(data.stationCollection.items[0]);
    }
  }, [isLoading, data]);

  if (error || data?.stationCollection.items.length === 0) return <ErrorPage />;

  if (isLoading || !data || !station) return null;

  const { uebungenCollection: { items: exercises } = [] } = station;

  const selectedExercise = exercises.filter(
    (ex) => ex.titel.toLowerCase() === exName.toLowerCase()
  );

  const listOptions = {
    renderNode: {
      [BLOCKS.UL_LIST]: (node, children) => <ul className={classes.list}>{children}</ul>,
      [BLOCKS.LIST_ITEM]: (node, children) => <li className={classes.listItem}>{children}</li>,
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <span style={{ display: "inlineBlock" }}>{children}</span>
      ),
    },
    renderText: (text) => text.replace(/\u00a0/g, " "),
  };

  const instruction = documentToReactComponents(selectedExercise[0].instruction.json, listOptions);
  const variation = documentToReactComponents(selectedExercise[0].variation?.json, listOptions);
  const tipp = documentToReactComponents(selectedExercise[0].tipp?.json, listOptions);

  const chartData = selectedExercise[0].musclesChart;

  return (
    <>
      <TitleBar
        title={exName.charAt(0).toUpperCase() + exName.slice(1)}
        navigateTo={`/stationen/${stNr}`}
        colorPrimary
      />
      <Wrapper>
        <CardMedia
          component="img"
          alt="Video-placeholder"
          height="170"
          image={selectedExercise[0].video.url}
          title="Video-placeholder"
        />
        <div className={classes.content}>
          <Typography variant="body1" className={classes.title}>
            Anleitung
          </Typography>
          <div>{instruction}</div>
          {variation && (
            <>
              <Typography variant="body1" className={classes.title}>
                {variation.length > 1 ? "Variationen" : "Variation"}
              </Typography>
              <div>{variation}</div>
            </>
          )}
          {tipp && (
            <>
              <Typography variant="body1" className={classes.title}>
                {tipp.length > 1 ? "Tipps" : "Tipp"}
              </Typography>
              <div>{tipp}</div>
            </>
          )}
          {chartData && (
            <>
              <Typography variant="body1" className={classes.title}>
                Muskelgruppen
              </Typography>
              <div className={classes.chartContainer}>
                <RadarChart chartData={chartData} />
              </div>
            </>
          )}
          {!chartData.percentageAbs && (
            <Typography variant="caption" className={classes.title}>
              Beim Laden des Diagramms ist ein Fehler aufgetreten
            </Typography>
          )}
        </div>
        <Card elevation={5} raised={true} className={classes.repetitionCard}>
          <div className={classes.repetitionContainer}>
            <Typography variant="body2" component="h3" className={classes.repetitionTitle}>
              Anzahl der Wiederholungen
            </Typography>
            <TextField
              className={classes.selector}
              type="tel"
              size="small"
              variant="outlined"
              placeholder="Wdh."
              error={repetition <= 0}
              value={repetition}
              InputProps={{
                classes: {
                  input: classes.resize,
                },
              }}
              onChange={(e) => setRepetition(e.target.value)}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={repetition <= 0}
            className={classes.button}
            onClick={() => {
              navigate(`excercise${verifiedString}`, { state: { repetition } });
            }}
          >
            Training beginnen
          </Button>
        </Card>
      </Wrapper>
    </>
  );
}

export default ExerciseDetails;
